import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { ArrowDownCircle, Building2, HandCoins, Plus, ReceiptText, X } from 'lucide-react';
import { db } from '../../services/firebase';
import { buildCollaboratorFinance, getDateValue, isInsidePeriod } from '../../utils/collaboratorFinance';
import './BusinessFinanceLedger.css';

const emptyForm = type => ({
  type,
  amount: '',
  date: new Date().toISOString().slice(0, 10),
  method: 'pix',
  collaboratorEmail: '',
  establishmentId: '',
  category: type === 'business_expense' ? 'operational' : 'commission',
  description: ''
});

const methods = { pix: 'PIX', cash: 'Dinheiro', bank_transfer: 'Transferência', credit_card: 'Cartão', other: 'Outro' };
const categories = {
  operational: 'Operacional', supplier: 'Fornecedor', maintenance: 'Manutenção',
  payroll: 'Pessoal', taxes: 'Impostos e taxas', marketing: 'Marketing', other: 'Outros'
};

const BusinessFinanceLedger = ({ companyId, userData }) => {
  const [entries, setEntries] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [establishments, setEstablishments] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [services, setServices] = useState([]);
  const [modalType, setModalType] = useState('');
  const [form, setForm] = useState(emptyForm('collaborator_payout'));
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', type: 'all', search: '' });
  const canCreate = userData?.role === 'admin';

  useEffect(() => {
    if (!companyId) return undefined;
    const subscriptions = [
      onSnapshot(collection(db, 'companies/' + companyId + '/financialEntries'), snap => setEntries(snap.docs.map(item => ({ id: item.id, ...item.data() })))),
      onSnapshot(collection(db, 'companies/' + companyId + '/collaborators'), snap => setCollaborators(snap.docs.map(item => ({ id: item.id, ...item.data() })))),
      onSnapshot(collection(db, 'companies/' + companyId + '/establishments'), snap => setEstablishments(snap.docs.map(item => ({ id: item.id, ...item.data() })))),
      onSnapshot(collection(db, 'companies/' + companyId + '/reservations'), snap => setReservations(snap.docs.map(item => ({ id: item.id, ...item.data() })))),
      onSnapshot(collection(db, 'companies/' + companyId + '/services'), snap => setServices(snap.docs.map(item => ({ id: item.id, ...item.data() }))))
    ];
    return () => subscriptions.forEach(unsubscribe => unsubscribe());
  }, [companyId]);

  const collaboratorBalances = useMemo(() => Object.fromEntries(collaborators.map(collaborator => {
    const email = collaborator.userEmail || collaborator.email || '';
    const finance = buildCollaboratorFinance({ reservations, services, entries, collaboratorEmail: email });
    return [email, finance];
  })), [collaborators, entries, reservations, services]);

  const filteredEntries = useMemo(() => entries.filter(entry => {
    if (filters.type !== 'all' && entry.type !== filters.type) return false;
    if (!isInsidePeriod(entry.date, filters.startDate, filters.endDate)) return false;
    if (filters.search) {
      const text = [entry.description, entry.collaboratorName, entry.collaboratorEmail, entry.establishmentName, entry.categoryLabel]
        .filter(Boolean).join(' ').toLowerCase();
      if (!text.includes(filters.search.toLowerCase())) return false;
    }
    return true;
  }).sort((a, b) => (getDateValue(b.date)?.getTime() || 0) - (getDateValue(a.date)?.getTime() || 0)), [entries, filters]);

  const summary = useMemo(() => filteredEntries.reduce((result, entry) => {
    const amount = Number(entry.amount) || 0;
    if (entry.type === 'collaborator_payout') result.payouts += amount;
    if (entry.type === 'business_expense') result.expenses += amount;
    result.total += amount;
    return result;
  }, { payouts: 0, expenses: 0, total: 0 }), [filteredEntries]);

  const openModal = type => {
    setForm(emptyForm(type));
    setModalType(type);
  };

  const saveEntry = async event => {
    event.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) return alert('Informe um valor válido.');
    const selectedCollaborator = collaborators.find(item => (item.userEmail || item.email) === form.collaboratorEmail);
    const selectedEstablishment = establishments.find(item => item.id === form.establishmentId);
    if (modalType === 'collaborator_payout') {
      if (!selectedCollaborator) return alert('Selecione o colaborador.');
      const available = collaboratorBalances[form.collaboratorEmail]?.pending || 0;
      if (amount > available + 0.009) return alert('O repasse não pode exceder o saldo a receber de ' + money(available) + '.');
    }
    if (!form.description.trim()) return alert('Informe a descrição do lançamento.');

    setSaving(true);
    try {
      await addDoc(collection(db, 'companies/' + companyId + '/financialEntries'), {
        type: modalType,
        amount,
        date: new Date(form.date + 'T12:00:00'),
        method: form.method,
        methodLabel: methods[form.method] || form.method,
        description: form.description.trim(),
        category: form.category,
        categoryLabel: modalType === 'business_expense' ? categories[form.category] : 'Repasse de comissão',
        collaboratorEmail: selectedCollaborator?.userEmail || selectedCollaborator?.email || '',
        collaboratorName: selectedCollaborator?.userName || selectedCollaborator?.name || '',
        establishmentId: selectedEstablishment?.id || '',
        establishmentName: selectedEstablishment?.name || '',
        companyId,
        createdByEmail: userData?.email || '',
        createdByName: userData?.name || userData?.email || 'Administrador',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setModalType('');
    } catch (error) {
      console.error('Erro ao salvar lançamento financeiro:', error);
      alert(error?.message || 'Erro ao salvar lançamento.');
    } finally {
      setSaving(false);
    }
  };

  const money = value => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  const date = value => getDateValue(value)?.toLocaleDateString('pt-BR') || '-';

  return <section className="business-ledger">
    <header className="business-ledger__header">
      <div><span>Livro de saídas</span><h2>Repasses e despesas</h2><p>Controle os pagamentos feitos pela empresa.</p></div>
      {canCreate && <div className="business-ledger__actions">
        <button type="button" onClick={() => openModal('collaborator_payout')}><HandCoins size={18} />Pagar colaborador</button>
        <button type="button" onClick={() => openModal('business_expense')}><Plus size={18} />Nova despesa</button>
      </div>}
    </header>

    <div className="business-ledger__summary">
      <div><HandCoins /><span>Repasses</span><strong>{money(summary.payouts)}</strong></div>
      <div><ReceiptText /><span>Outras despesas</span><strong>{money(summary.expenses)}</strong></div>
      <div className="is-total"><ArrowDownCircle /><span>Total de saídas</span><strong>{money(summary.total)}</strong></div>
    </div>

    <div className="business-ledger__filters">
      <input type="search" placeholder="Buscar lançamento..." value={filters.search} onChange={event => setFilters({ ...filters, search: event.target.value })} />
      <select value={filters.type} onChange={event => setFilters({ ...filters, type: event.target.value })}>
        <option value="all">Todas as saídas</option><option value="collaborator_payout">Repasses</option><option value="business_expense">Despesas</option>
      </select>
      <input type="date" value={filters.startDate} onChange={event => setFilters({ ...filters, startDate: event.target.value })} aria-label="Data inicial das saídas" />
      <input type="date" value={filters.endDate} onChange={event => setFilters({ ...filters, endDate: event.target.value })} aria-label="Data final das saídas" />
    </div>

    {filteredEntries.length === 0 ? <p className="business-ledger__empty">Nenhuma saída registrada no período.</p> :
      <div className="business-ledger__table-wrap"><table>
        <thead><tr><th>Data</th><th>Tipo</th><th>Descrição</th><th>Referência</th><th>Forma</th><th>Valor</th></tr></thead>
        <tbody>{filteredEntries.map(entry => <tr key={entry.id}>
          <td data-label="Data">{date(entry.date)}</td>
          <td data-label="Tipo"><span className={'ledger-type ' + (entry.type === 'collaborator_payout' ? 'is-payout' : 'is-expense')}>{entry.type === 'collaborator_payout' ? 'Repasse' : entry.categoryLabel || 'Despesa'}</span></td>
          <td data-label="Descrição"><strong>{entry.description}</strong><small>{entry.createdByName || entry.createdByEmail}</small></td>
          <td data-label="Referência">{entry.collaboratorName || entry.establishmentName || 'Empresa'}</td><td data-label="Forma">{entry.methodLabel || methods[entry.method] || '-'}</td>
          <td data-label="Valor" className="ledger-amount">{money(entry.amount)}</td>
        </tr>)}</tbody>
      </table></div>}

    {modalType && <div className="business-ledger__overlay" onClick={() => setModalType('')}>
      <form className="business-ledger__modal" onSubmit={saveEntry} onClick={event => event.stopPropagation()}>
        <header><div><span>Novo lançamento</span><h3>{modalType === 'collaborator_payout' ? 'Pagamento ao colaborador' : 'Despesa do negócio'}</h3></div><button type="button" onClick={() => setModalType('')} title="Fechar"><X /></button></header>
        {modalType === 'collaborator_payout' && <label>Colaborador *
          <select value={form.collaboratorEmail} onChange={event => setForm({ ...form, collaboratorEmail: event.target.value })} required>
            <option value="">Selecione</option>{collaborators.map(item => {
              const email = item.userEmail || item.email;
              return <option key={item.id} value={email}>{item.userName || item.name} · saldo {money(collaboratorBalances[email]?.pending)}</option>;
            })}
          </select>
        </label>}
        {modalType === 'business_expense' && <label>Categoria *
          <select value={form.category} onChange={event => setForm({ ...form, category: event.target.value })}>{Object.entries(categories).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </label>}
        <div className="business-ledger__two-cols">
          <label>Valor *<input type="number" min="0.01" step="0.01" value={form.amount} onChange={event => setForm({ ...form, amount: event.target.value })} required /></label>
          <label>Data *<input type="date" value={form.date} onChange={event => setForm({ ...form, date: event.target.value })} required /></label>
        </div>
        <label>Forma de pagamento<select value={form.method} onChange={event => setForm({ ...form, method: event.target.value })}>{Object.entries(methods).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label><span className="label-with-icon"><Building2 size={16} />Ponto de venda (opcional)</span>
          <select value={form.establishmentId} onChange={event => setForm({ ...form, establishmentId: event.target.value })}>
            <option value="">Sem vínculo</option>{establishments.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>Descrição *<textarea rows="3" value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} required /></label>
        <footer><button type="button" onClick={() => setModalType('')}>Cancelar</button><button className="is-primary" disabled={saving} type="submit">{saving ? 'Salvando...' : 'Registrar saída'}</button></footer>
      </form>
    </div>}
  </section>;
};

export default BusinessFinanceLedger;
